import { titleCase } from "@multicines/utils";
import { ScrollableTabs, Modal } from "artisn-ui-react";
import ArrowDownSVG from "images/arrow-down-small.svg";
import CloseSVG from "images/close.svg";
import React, { useCallback, useMemo, useState } from "react";

import { getMovieFilters } from "./MovieDetailFilter.helpers";
import Styles from "./MovieDetailFilter.styles";
import { MovieDetailFilterProps as Props } from "./MovieDetailFilter.types";
import Button from "components/global/Button/Button";
import SelectInputOptions from "components/global/SelectInputOptions/SelectInputOptions";
import useI18n from "hooks/useI18n";

const MovieDetailFilter: React.FC<Props> = props => {
  const { setRoom, setFormat, language, format, room } = props;
  const { movie, sessionTypes, setLanguage } = props;
  const [showModal, setShowModal] = useState(false);
  const [itemSelected, setItemSelected] = useState<string | undefined>();
  const t = useI18n();

  const languages = useMemo(() => movie?.languages || [], [movie]);

  const theaterTypesName = useMemo(() => {
    if (!sessionTypes) return [];
    return [
      ...Array.from(
        new Set(
          sessionTypes.flatMap(sessionType =>
            sessionType.theaterTypes.flatMap(theaterType => theaterType.name)
          )
        )
      )
    ];
  }, [sessionTypes]);

  const theaterTypes = useMemo(() => {
    if (!sessionTypes) return [];
    return sessionTypes.flatMap(sessionType => sessionType.theaterTypes);
  }, [sessionTypes]);

  const theaterTypesSessions = useMemo(
    () => theaterTypes?.flatMap(theaterType => theaterType.sessions),
    [theaterTypes]
  );

  const sessionAttributesNames = useMemo(() => {
    return Array.from(
      new Set(
        theaterTypesSessions?.flatMap(
          item => item.sessionAttributesNames?.[0] ?? ""
        )
      )
    );
  }, [theaterTypesSessions]);

  const filters = useMemo(() => {
    return getMovieFilters(
      t,
      language,
      languages,
      theaterTypesName,
      format,
      room,
      sessionAttributesNames
    );
  }, [
    t,
    language,
    languages,
    theaterTypesName,
    format,
    room,
    sessionAttributesNames
  ]);

  const selectedOptionsHandler = useCallback(
    (options: string | undefined) => {
      const value = itemSelected ? itemSelected : [];
      const optionType = Object.values(value)[2];
      if (optionType === "language") {
        setLanguage(options);
      }
      if (optionType === "room") {
        setRoom(options);
      }
      if (optionType === "format") {
        setFormat(options);
      }
    },
    [itemSelected, setFormat, setLanguage, setRoom]
  );

  const renderItem = useCallback(
    // @ts-ignore
    (listItem, index) => {
      const defaultValue = listItem.defaultValue;
      const value = listItem.value;
      return (
        <div key={index} className="MovieDetailFilter__scrollable">
          <Button
            className="MovieDetailFilter__button"
            onClick={() => {
              setShowModal(true);
              setItemSelected(listItem);
            }}
          >
            <p className="MovieDetailFilter__text">
              {titleCase(value || defaultValue)}
            </p>
            <ArrowDownSVG className="Navbar__top__icon" />
          </Button>
          <Modal
            color="transparent"
            closeIcon={<CloseSVG />}
            opened={showModal}
            onClose={() => setShowModal(false)}
          >
            <SelectInputOptions
              setShowModal={setShowModal}
              itemSelected={itemSelected}
              onSelectedOption={selectedOptionsHandler}
            />
          </Modal>
        </div>
      );
    },
    [itemSelected, selectedOptionsHandler, showModal]
  );

  return (
    <Styles className="MovieDetailFilter">
      <ScrollableTabs
        className="MovieDetailFilter__tabs"
        activeClassName="MovieDetailFilter__item--active"
      >
        <div className="MovieDetailFilter__filters">
          {filters.map((item, index) => {
            return renderItem(item, index);
          })}
        </div>
      </ScrollableTabs>
    </Styles>
  );
};

export default MovieDetailFilter;
